import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    feedbackId: String,
    validate: { type: Boolean, default: false }
  }

  connect() {
    this.element.autocapitalize = "characters"
    this.element.maxLength = 18
    this.element.inputMode = "text"
    this.validationVisible = false

    this.format()
  }

  format() {
    this.element.value = this.formatFederalTaxId(this.element.value)

    if (this.validateValue) {
      this.validateField()
    }
  }

  validate() {
    this.validationVisible = true
    this.validateField()
  }

  showValidation() {
    this.validationVisible = true
    this.validateField()
  }

  validateField() {
    const message = this.validationMessage(this.normalize(this.element.value))
    this.element.setCustomValidity(message)

    const shouldShow = this.validationVisible && (message || this.element.value.trim())
    this.element.classList.toggle("is-invalid", Boolean(shouldShow && message))
    this.element.classList.toggle("is-valid", Boolean(shouldShow && !message))

    const feedback = this.feedbackElement
    if (feedback) feedback.textContent = message
  }

  formatFederalTaxId(value) {
    const normalized = this.normalize(value)

    if (!/[A-Z]/.test(normalized) && normalized.length <= 11) {
      return this.formatCpf(normalized)
    }

    return this.formatCnpj(normalized)
  }

  formatCpf(value) {
    const digits = value.replace(/\D/g, "").slice(0, 11)
    const parts = [
      digits.slice(0, 3),
      digits.slice(3, 6),
      digits.slice(6, 9),
      digits.slice(9, 11)
    ].filter(Boolean)

    if (parts.length <= 1) return parts[0] || ""
    if (parts.length === 2) return `${parts[0]}.${parts[1]}`
    if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`

    return `${parts[0]}.${parts[1]}.${parts[2]}-${parts[3]}`
  }

  formatCnpj(value) {
    const base = value.slice(0, 12)
    const checkDigits = value.slice(12).replace(/\D/g, "").slice(0, 2)
    const cnpj = `${base}${checkDigits}`.slice(0, 14)
    const parts = [
      cnpj.slice(0, 2),
      cnpj.slice(2, 5),
      cnpj.slice(5, 8),
      cnpj.slice(8, 12),
      cnpj.slice(12, 14)
    ].filter(Boolean)

    if (parts.length <= 1) return parts[0] || ""
    if (parts.length === 2) return `${parts[0]}.${parts[1]}`
    if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`
    if (parts.length === 4) return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}`

    return `${parts[0]}.${parts[1]}.${parts[2]}/${parts[3]}-${parts[4]}`
  }

  validationMessage(value) {
    if (!value) return "Informe um CPF ou CNPJ."

    if (this.isCpf(value)) {
      return this.validCpf(value) ? "" : "CPF inválido. Confira os números digitados."
    }

    if (this.isCnpj(value)) {
      return this.validCnpj(value) ? "" : "CNPJ inválido. Confira os caracteres digitados."
    }

    if (/[A-Z]/.test(value)) {
      return "CNPJ alfanumérico deve ter 14 caracteres, com 2 dígitos finais."
    }

    return "CPF/CNPJ deve ter 11 dígitos para CPF ou 14 caracteres para CNPJ."
  }

  validCpf(value) {
    if (!this.isCpf(value)) return false
    if (new Set(value.split("")).size === 1) return false

    const numbers = value.split("").map((digit) => Number(digit))
    const firstDigit = this.cpfVerifierDigit(numbers.slice(0, 9), 10)
    const secondDigit = this.cpfVerifierDigit([...numbers.slice(0, 9), firstDigit], 11)

    return numbers[9] === firstDigit && numbers[10] === secondDigit
  }

  cpfVerifierDigit(numbers, weightStart) {
    const sum = numbers.reduce((total, number, index) => total + number * (weightStart - index), 0)
    const remainder = (sum * 10) % 11

    return remainder === 10 ? 0 : remainder
  }

  validCnpj(value) {
    if (!this.isCnpj(value)) return false
    if (/^(\d)\1{13}$/.test(value)) return false

    const numbers = value.slice(0, 12).split("").map((character) => this.cnpjCharacterValue(character))
    const firstDigit = this.cnpjVerifierDigit(numbers)
    const secondDigit = this.cnpjVerifierDigit([...numbers, firstDigit])

    return Number(value[12]) === firstDigit && Number(value[13]) === secondDigit
  }

  cnpjVerifierDigit(numbers) {
    const weights = numbers.length === 12
      ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

    const sum = numbers.reduce((total, number, index) => total + number * weights[index], 0)
    const remainder = sum % 11

    return remainder < 2 ? 0 : 11 - remainder
  }

  cnpjCharacterValue(character) {
    return character.charCodeAt(0) - 48
  }

  isCpf(value) {
    return /^\d{11}$/.test(value)
  }

  isCnpj(value) {
    return /^[A-Z0-9]{12}\d{2}$/.test(value)
  }

  normalize(value) {
    return String(value || "")
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
  }

  get feedbackElement() {
    if (!this.hasFeedbackIdValue) return null

    return document.getElementById(this.feedbackIdValue)
  }
}
